import React from 'react';
import { Thermometer, Droplets, Zap, Sun, Users } from 'lucide-react';

export interface SensorDisplayStyle05Props {
  type?: 'temperature' | 'humidity' | 'power' | 'lux' | 'occupancy';
  value?: number | boolean;
  label?: string;
  unit?: string;
  size?: 'small' | 'medium' | 'large';
}

const SENSOR_CONFIG: Record<string, {
  icon: React.ElementType;
  color: string;
  bgGradient: string;
  borderColor: string;
  unit: string;
  label: string;
  formatValue: (v: any) => string;
}> = {
  temperature: {
    icon: Thermometer,
    color: 'text-orange-400',
    bgGradient: 'from-orange-900/40 to-red-900/30',
    borderColor: 'border-orange-500/20',
    unit: '°C',
    label: '溫度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  humidity: {
    icon: Droplets,
    color: 'text-blue-400',
    bgGradient: 'from-blue-900/40 to-cyan-900/30',
    borderColor: 'border-blue-500/20',
    unit: '%',
    label: '濕度',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  power: {
    icon: Zap,
    color: 'text-yellow-400',
    bgGradient: 'from-yellow-900/40 to-amber-900/30',
    borderColor: 'border-yellow-500/20',
    unit: 'W',
    label: '功率',
    formatValue: (v) => {
      if (typeof v !== 'number') return '--';
      return v >= 1000 ? `${(v / 1000).toFixed(2)}kW` : `${v.toFixed(0)}W`;
    }
  },
  voltage: {
    icon: Zap,
    color: 'text-yellow-300',
    bgGradient: 'from-yellow-900/40 to-amber-900/30',
    borderColor: 'border-yellow-400/20',
    unit: 'V',
    label: '電壓',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(1) : '--'
  },
  current: {
    icon: Zap,
    color: 'text-orange-300',
    bgGradient: 'from-orange-900/40 to-red-900/30',
    borderColor: 'border-orange-400/20',
    unit: 'A',
    label: '電流',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  energy: {
    icon: Zap,
    color: 'text-green-400',
    bgGradient: 'from-green-900/40 to-emerald-900/30',
    borderColor: 'border-green-500/20',
    unit: 'kWh',
    label: '累積用電',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(2) : '--'
  },
  lux: {
    icon: Sun,
    color: 'text-amber-400',
    bgGradient: 'from-amber-900/40 to-yellow-900/30',
    borderColor: 'border-amber-500/20',
    unit: 'lux',
    label: '光照',
    formatValue: (v) => typeof v === 'number' ? v.toFixed(0) : '--'
  },
  occupancy: {
    icon: Users,
    color: 'text-purple-400',
    bgGradient: 'from-purple-900/40 to-pink-900/30',
    borderColor: 'border-purple-500/20',
    unit: '',
    label: '佔用狀態',
    formatValue: (v) => v ? '有人' : '無人'
  }
};

/**
 * SensorDisplay Style 05 - Rectangle
 * Rounded rectangle with gradient background and icon
 * Uses type parameter to determine icon, color, and gradient
 */
export const Style05_Rectangle: React.FC<SensorDisplayStyle05Props> = ({
  type = 'temperature',
  value,
  label,
  unit,
  size = 'medium'
}) => {
  const config = SENSOR_CONFIG[type] || SENSOR_CONFIG.temperature;
  const Icon = config.icon;
  const displayLabel = label ?? config.label;
  const displayUnit = unit ?? config.unit;
  const displayValue = config.formatValue(value);
  const valueWithUnit = type === 'power' ? displayValue : `${displayValue}${displayUnit}`;

  // Size variants for responsive design
  const sizeStyles = {
    small: {
      container: 'px-4 py-3 rounded-xl',
      icon: 'w-6 h-6 mb-1',
      label: 'text-[10px]',
      value: 'text-xl'
    },
    medium: {
      container: 'px-6 py-4 rounded-2xl',
      icon: 'w-8 h-8 mb-1.5',
      label: 'text-xs',
      value: 'text-2xl'
    },
    large: {
      container: 'px-8 py-5 rounded-2xl',
      icon: 'w-10 h-10 mb-2',
      label: 'text-xs',
      value: 'text-3xl'
    }
  };

  const styles = sizeStyles[size] || sizeStyles.medium;

  return (
    <div className={`bg-gradient-to-br ${config.bgGradient} ${styles.container} text-center border ${config.borderColor} w-full`}>
      <Icon className={`${styles.icon} ${config.color} mx-auto`} />
      <p className={`${styles.label} text-slate-400 mb-0.5`}>{displayLabel}</p>
      <p className={`${styles.value} font-bold ${config.color}`}>
        {valueWithUnit}
      </p>
    </div>
  );
};

export default Style05_Rectangle;
